import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { useNavigate } from 'react-router';

import {
  deleteDatastore,
  fetchDatastores,
} from '../../../../actions/datastores';

import DatastoreGridToolbar from './DatastoreGridToolbar';
import CopyableGridCell from '@/components/shared/CopyableGridCell';

export default function Datastores({ region, autoFetch = true }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUser();
  const datastores = useSelector((state) => state.datastores);
  const [transitioningDatastores, setTransitioningDatastores] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => {
    (async () => {
      if (autoFetch && datastores === null) {
        dispatch(fetchDatastores(region.provider_uuid, region.region_uuid));
        return;
      }
    })();
  }, [dispatch, region, datastores, autoFetch]);

  const editDatastore = useCallback(
    (datastore) => () => {
      navigate(`/resources/provider/${region.provider_uuid}/region/${region.region_uuid}/datastore/edit?datastore=${datastore.store_id}`);
    },
    [navigate, region]
  );
  const removeDatastore = useCallback(
    (datastore) => async () => {
      setTransitioningDatastores((prevTransitioningDatastores) => [
        ...prevTransitioningDatastores,
        datastore.store_id,
      ]);
      await dispatch(
        deleteDatastore(
          region.provider_uuid,
          region.region_uuid,
          datastore.store_id
        )
      );
      setTransitioningDatastores((prevTransitioningDatastores) =>
        prevTransitioningDatastores.filter((id) => id !== datastore.store_id)
      );
    },
    [dispatch, region]
  );

  const renderStatus = useCallback(
    (params) => {
      if (transitioningDatastores.includes(params.id)) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={20} />
          </Box>
        );
      } else {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Stack direction='row' spacing={1}>
              <CheckCircleIcon color='success' />
            </Stack>
          </Box>
        );
      }
    },
    [transitioningDatastores]
  );

  const columns = useMemo(
    () => [
      {
        field: 'store_id',
        headerName: 'ID',
        type: 'string',
        flex: 1.25,
        renderCell: (params) => <CopyableGridCell value={params.value} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: renderStatus,
        flex: 0.25,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        flex: 0.5,
      },
      {
        field: 'uri',
        headerName: 'URI/Address',
        type: 'string',
        flex: 0.5,
      },
      {
        field: 'root',
        headerName: 'Root Path',
        type: 'string',
        flex: 1,
      },

      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            onClick={editDatastore(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Remove'
            onClick={removeDatastore(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [editDatastore, removeDatastore, renderStatus]
  );

  const Toolbar = () => (
    <DatastoreGridToolbar
      region={region}
      disabled={!user.cloudbender_enabled}
    />
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Datastores
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                getRowId={(row) => row.store_id}
                columns={columns}
                rows={datastores || []}
                slots={{
                  toolbar: Toolbar,
                }}
                showToolbar
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
              />
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
